import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarClock, Plus, Trash2, Send, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { label: "Lembrete de pagamento", text: "Olá! Passando para lembrar que seu pagamento vence em breve. Se precisar da 2ª via, é só responder por aqui." },
  { label: "Retorno de orçamento", text: "Olá! Conseguiu avaliar o orçamento que enviamos? Fico à disposição para tirar dúvidas." },
  { label: "Confirmação de agendamento", text: "Olá! Confirmando seu horário agendado conosco. Responda SIM para confirmar ou nos avise se precisar remarcar." },
  { label: "Promoção / novidade", text: "Olá! Temos novidades especiais para você esta semana. Quer saber mais?" },
];

function fmt(iso) { return new Date(iso).toLocaleString("pt-BR"); }

export default function SchedulerPage() {
  const [items, setItems] = useState([]);
  const [connections, setConnections] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [broadcastMode, setBroadcastMode] = useState(false);
  const [form, setForm] = useState({ connection_id: "", phone: "", text: "", scheduled_for: "", recurring: "", label: "" });
  const [selectedPhones, setSelectedPhones] = useState([]);

  const load = async () => {
    const { data } = await api.get("/scheduled");
    setItems(data);
  };
  const loadAux = async () => {
    const [c, co] = await Promise.all([api.get("/connections"), api.get("/contacts")]);
    setConnections(c.data.filter(x => x.status === "connected" || x.status === "qr" || true));
    setContacts(co.data);
  };
  useEffect(() => { load(); loadAux(); }, []);

  const togglePhone = (p) => {
    setSelectedPhones((prev) => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const create = async () => {
    if (!form.connection_id || !form.text || !form.scheduled_for) {
      toast.error("Preencha conexão, texto e data"); return;
    }
    const isoLocal = new Date(form.scheduled_for).toISOString();
    try {
      if (broadcastMode) {
        if (selectedPhones.length === 0) { toast.error("Selecione pelo menos um contato"); return; }
        await api.post("/scheduled/broadcast", {
          connection_id: form.connection_id,
          phones: selectedPhones,
          text: form.text,
          scheduled_for: isoLocal,
          label: form.label || "Envio em lote",
        });
        toast.success(`${selectedPhones.length} mensagens agendadas`);
      } else {
        if (!form.phone) { toast.error("Informe o telefone"); return; }
        await api.post("/scheduled", {
          ...form,
          phone: form.phone.replace(/\D/g, ""),
          scheduled_for: isoLocal,
          recurring: form.recurring || null,
        });
        toast.success("Mensagem agendada");
      }
      setOpen(false); setSelectedPhones([]);
      setForm({ connection_id: "", phone: "", text: "", scheduled_for: "", recurring: "", label: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao agendar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este agendamento?")) return;
    await api.delete(`/scheduled/${id}`);
    load();
  };

  const applyPreset = (p) => setForm((f) => ({ ...f, text: p.text, label: p.label }));

  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="heading text-3xl font-extrabold flex items-center gap-2">
              <CalendarClock className="w-7 h-7 text-emerald-600" /> Agendador de Mensagens
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Agende lembretes, retornos e campanhas — por cliente ou em lote.
            </p>
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setBroadcastMode(false); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="scheduler-new-button">
                <Plus className="w-4 h-4 mr-2" /> Novo agendamento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Novo agendamento</DialogTitle>
                <DialogDescription>Escolha envio individual ou em lote para múltiplos clientes.</DialogDescription>
              </DialogHeader>
              <div className="flex gap-2 mb-2">
                <Button size="sm" variant={!broadcastMode ? "default" : "outline"} onClick={() => setBroadcastMode(false)} data-testid="scheduler-mode-individual"
                        className={!broadcastMode ? "bg-emerald-600 hover:bg-emerald-700" : ""}>
                  <Send className="w-4 h-4 mr-1" /> Individual
                </Button>
                <Button size="sm" variant={broadcastMode ? "default" : "outline"} onClick={() => setBroadcastMode(true)} data-testid="scheduler-mode-broadcast"
                        className={broadcastMode ? "bg-emerald-600 hover:bg-emerald-700" : ""}>
                  <UsersIcon className="w-4 h-4 mr-1" /> Em lote
                </Button>
              </div>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <Label>Conexão WhatsApp</Label>
                  <Select value={form.connection_id} onValueChange={(v) => setForm({ ...form, connection_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="scheduler-connection-select"><SelectValue placeholder="Escolha a conexão" /></SelectTrigger>
                    <SelectContent>
                      {connections.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` · +${c.phone}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {broadcastMode ? (
                  <div>
                    <Label>Contatos ({selectedPhones.length} selecionados)</Label>
                    <div className="mt-1 border border-border rounded-lg max-h-48 overflow-y-auto">
                      {contacts.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">Nenhum contato cadastrado ainda.</div>}
                      {contacts.filter(c => /^\d{10,13}$/.test(c.phone || "")).map(c => (
                        <label key={c.phone} className="flex items-center gap-3 p-2 hover:bg-muted/60 cursor-pointer border-b border-border/60">
                          <Checkbox data-testid="scheduler-contact-checkbox" checked={selectedPhones.includes(c.phone)} onCheckedChange={() => togglePhone(c.phone)} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{c.name || c.phone}</div>
                            <div className="text-[11px] text-muted-foreground mono">+{c.phone}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label>Telefone (apenas dígitos, com DDI/DDD)</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="5511999999999" data-testid="scheduler-phone-input" />
                  </div>
                )}
                <div>
                  <Label>Data e hora</Label>
                  <Input type="datetime-local" data-testid="scheduler-datetime-input" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} />
                </div>
                {!broadcastMode && (
                  <div>
                    <Label>Repetir</Label>
                    <Select value={form.recurring} onValueChange={(v) => setForm({ ...form, recurring: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Sem repetição" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem repetição</SelectItem>
                        <SelectItem value="daily">Diariamente</SelectItem>
                        <SelectItem value="monthly">Mensalmente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Rótulo (opcional)</Label>
                  <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex.: Lembrete de pagamento - Maio" />
                </div>
                <div>
                  <Label>Mensagem</Label>
                  <div className="flex flex-wrap gap-1.5 my-2">
                    {PRESETS.map(p => (
                      <button key={p.label} type="button" data-testid="scheduler-preset-button" onClick={() => applyPreset(p)}
                              className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-200 border border-emerald-200/60 hover:bg-emerald-100">
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <Textarea rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Digite a mensagem..." data-testid="scheduler-text-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} data-testid="scheduler-submit-button" className="bg-emerald-600 hover:bg-emerald-700">
                  {broadcastMode ? `Agendar ${selectedPhones.length} ${selectedPhones.length === 1 ? "envio" : "envios"}` : "Agendar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">
                Nenhum agendamento. Crie lembretes ou campanhas para seus clientes.
              </div>
            ) : items.map((s) => (
              <div key={s.id} data-testid="scheduler-item" className="flex items-start gap-4 p-4 border-b border-border">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center text-emerald-700 dark:text-emerald-200">
                  <CalendarClock className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{s.label || "Agendamento"}</span>
                    <Badge className={
                      s.status === "pending" ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" :
                      s.status === "sent" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" :
                      "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200"
                    }>{{ pending: "Pendente", sent: "Enviado", failed: "Falhou" }[s.status] || s.status}</Badge>
                    {s.recurring && <Badge variant="outline" className="text-[10px]">{s.recurring === "daily" ? "Diário" : "Mensal"}</Badge>}
                    <span className="text-xs text-muted-foreground mono">+{s.phone}</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.text}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{fmt(s.scheduled_for)} · por {s.created_by_name}</div>
                </div>
                {s.status === "pending" && (
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(s.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
